import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Bar,
  BarChart,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { useRangeKey } from '../TimeRangeControl';
import { fetchTtps } from '../api';
import ChartTooltip from '../charts/ChartTooltip';
import type { TtpsPayload } from '../types';
import { entityHref } from '../util';
import { BuiltAt, QUERY_OPTIONS, TabStatus } from './_shared';

type Actor = TtpsPayload['per_actor'][number];
type Technique = Actor['techniques'][number];

// MITRE ATT&CK tactic palette. Falls back to a neutral grey.
const TACTIC_COLOR: Record<string, string> = {
  'initial-access':         '#e07060',
  'execution':              '#f0b440',
  'persistence':            '#7fc94c',
  'privilege-escalation':   '#56b3c4',
  'defense-evasion':        '#6c8cff',
  'credential-access':      '#b88cff',
  'discovery':              '#e070b8',
  'lateral-movement':       '#a09080',
  'collection':             '#d4a017',
  'command-and-control':    '#52a5e0',
  'exfiltration':           '#c44f3e',
  'impact':                 '#8b5cc4',
  'reconnaissance':         '#5cbf85',
  'resource-development':   '#bf855c',
};

function tacticColor(tactic: string): string {
  return TACTIC_COLOR[tactic] || '#888888';
}

function ActorPanel({ actor }: { actor: Actor }) {
  const total = useMemo(
    () => actor.techniques.reduce((acc, t) => acc + t.monthly.reduce((a, m) => a + m.n, 0), 0),
    [actor.techniques],
  );
  const allMonths = useMemo(() => {
    const s = new Set<string>();
    for (const t of actor.techniques) for (const r of t.monthly) s.add(r.ym);
    return Array.from(s).sort();
  }, [actor.techniques]);

  // For each technique, render a horizontal mini-bar by month.
  const data = useMemo(() => {
    return actor.techniques.map((t: Technique) => {
      const byMonth: Record<string, number> = {};
      for (const r of t.monthly) byMonth[r.ym] = r.n;
      return {
        technique_id: t.technique_id,
        technique_name: t.technique_name,
        tactic: t.tactic,
        total: t.monthly.reduce((a, m) => a + m.n, 0),
        ...byMonth,
      };
    });
  }, [actor.techniques]);

  if (actor.techniques.length === 0) return null;

  const actorHref = entityHref('ThreatActor', actor.actor_name);
  return (
    <div className="trends-ttps-actor">
      <div className="trends-ttps-actor-head">
        {actorHref ? (
          <Link to={actorHref} className="trends-entity-link">
            <strong>{actor.actor_name}</strong>
          </Link>
        ) : (
          <strong>{actor.actor_name}</strong>
        )}
        <span className="trends-muted">
          {actor.techniques.length} techniques · {total} mentions · {allMonths.length} months
        </span>
      </div>
      <ResponsiveContainer width="100%" height={Math.max(180, data.length * 22)}>
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, bottom: 4, left: 4 }}>
          <XAxis type="number" hide />
          <YAxis
            dataKey="technique_id"
            type="category"
            fontSize={10}
            tickMargin={4}
            width={88}
            tick={({ x, y, payload }) => (
              <text x={x} y={y} dy={3} textAnchor="end" fontSize={10} fill="var(--text-muted, #999)">
                {payload.value}
              </text>
            )}
          />
          <Tooltip
            content={
              <ChartTooltip
                labelFormatter={(label, items) => {
                  const row = (items?.[0] as { payload?: { technique_id?: string; technique_name?: string } } | undefined)?.payload;
                  return row?.technique_id
                    ? `${row.technique_id} — ${row.technique_name ?? ''}`.trim()
                    : String(label ?? '');
                }}
                valueFormatter={(v) => `${v} mentions`}
              />
            }
          />
          <Bar dataKey="total" name="mentions" isAnimationActive={false}>
            {data.map((row, i) => (
              <Cell key={i} fill={tacticColor(row.tactic)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function TacticLegend({ actors }: { actors: Actor[] }) {
  const tactics = useMemo(() => {
    const s = new Set<string>();
    for (const a of actors) for (const t of a.techniques) if (t.tactic) s.add(t.tactic);
    return Array.from(s).sort();
  }, [actors]);
  if (tactics.length === 0) return null;
  return (
    <div className="trends-ttps-legend">
      {tactics.map((t) => (
        <span key={t} className="trends-ttps-legend-item">
          <span className="trends-ttps-legend-swatch" style={{ background: tacticColor(t) }} />
          {t}
        </span>
      ))}
    </div>
  );
}

export default function TtpsTab() {
  const range = useRangeKey();
  const q = useQuery({
    queryKey: ['trends', 'ttps', range],
    queryFn: () => fetchTtps(range),
    ...QUERY_OPTIONS,
  });

  return (
    <section className="trends-tab-content trends-ttps">
      <TabStatus
        isLoading={q.isLoading}
        isError={q.isError}
        error={q.error}
        data={q.data}
        emptyHint="TTPs need ThreatActor REFERENCED_IN edges and IOC→REFERENCES_TECHNIQUE→AttackTechnique edges. Run trends_snapshot once the corpus + ATT&CK loaders have completed."
      >
        {q.data && (
          <>
            <BuiltAt at={q.data.built_at} />
            <TacticLegend actors={q.data.per_actor} />
            <div className="trends-ttps-grid">
              {q.data.per_actor.map((a) => (
                <ActorPanel key={a.actor_id} actor={a} />
              ))}
            </div>
          </>
        )}
      </TabStatus>
    </section>
  );
}
